/* eslint-disable max-statements */
import { useEffect, useState, useCallback, useRef } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { JobTrackingResponseDto } from '@tiendanube/common';
import { AsyncDocumentJobStatus } from '@tiendanube/common/src/enums';
import { useToastProgress } from 'App/components/ToastProgressContext';
import { useAppDispatch } from 'App/store';
import { getJobStatus } from 'domains/FulfillmentOrders/fulfillmentOrdersSlice/fulfillmentOrdersSlice';
import useTranslationFulfillmentOrders from 'domains/FulfillmentOrders/useTranslationFulfillmentOrders';

export interface UseAsyncDocumentPollingResult {
  isPolling: boolean;
  progress: number;
  status: AsyncDocumentJobStatus | null;
}

const POLLING_INTERVALS = [1000, 2000, 3000, 4000, 8000, 16000];
const MAX_POLLING_TIME = 5 * 60 * 1000;

function useAsyncDocumentPolling(
  jobId: string | null,
  onComplete: (url: string) => void,
  onError: (error: string) => void,
  onTimeout: () => void,
): UseAsyncDocumentPollingResult {
  const dispatch = useAppDispatch();
  const t = useTranslationFulfillmentOrders();
  const { addToastProgress } = useToastProgress();
  const [isPolling, setIsPolling] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<AsyncDocumentJobStatus | null>(null);

  const pollingCountRef = useRef(0);
  const startTimeRef = useRef<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);
  const currentJobIdRef = useRef<string | null>(null);

  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  const onTimeoutRef = useRef(onTimeout);

  useEffect(() => {
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
    onTimeoutRef.current = onTimeout;
  }, [onComplete, onError, onTimeout]);

  useEffect(() => {
    if (isPolling) {
      let message = '';
      let showProgress = false;

      if (status === AsyncDocumentJobStatus.PENDING || progress < 5) {
        message = t('printManager.asyncProcessing');
      } else if (progress >= 95) {
        message = t('printManager.asyncFinalizing');
      } else {
        message = t('printManager.asyncGenerating');
        showProgress = true;
      }

      addToastProgress({
        label: message,
        number: showProgress ? progress : undefined,
      });
    }
  }, [isPolling, progress, status, addToastProgress, t]);

  const stopPolling = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsPolling(false);
    isPollingRef.current = false;
    pollingCountRef.current = 0;
  }, []);

  const poll = useCallback(async () => {
    if (!jobId || !isPollingRef.current || currentJobIdRef.current !== jobId) {
      return;
    }

    try {
      const actionPayload = await dispatch(getJobStatus(jobId));
      const result: JobTrackingResponseDto = await unwrapResult(actionPayload);

      if (!isPollingRef.current || currentJobIdRef.current !== jobId) {
        return;
      }

      setProgress(result.progress);
      setStatus(result.status);

      if (result.status === AsyncDocumentJobStatus.SUCCESS && result.url) {
        stopPolling();
        onCompleteRef.current(result.url);
        return;
      }

      if (result.status === AsyncDocumentJobStatus.FAILURE) {
        stopPolling();
        onErrorRef.current(
          result.error?.message || 'Failed to generate document',
        );
        return;
      }

      if (
        result.status === AsyncDocumentJobStatus.PENDING ||
        result.status === AsyncDocumentJobStatus.IN_PROCESSING
      ) {
        const elapsedTime = Date.now() - startTimeRef.current;

        if (elapsedTime >= MAX_POLLING_TIME) {
          stopPolling();
          onTimeoutRef.current();
          return;
        }

        const intervalIndex = Math.min(
          pollingCountRef.current,
          POLLING_INTERVALS.length - 1,
        );
        const nextInterval = POLLING_INTERVALS[intervalIndex];

        pollingCountRef.current += 1;

        timeoutRef.current = setTimeout(() => {
          poll();
        }, nextInterval);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      stopPolling();
      onErrorRef.current(errorMsg);
    }
  }, [jobId, dispatch, stopPolling]);

  useEffect(() => {
    if (jobId && !isPollingRef.current) {
      setIsPolling(true);
      isPollingRef.current = true;
      currentJobIdRef.current = jobId;
      startTimeRef.current = Date.now();
      pollingCountRef.current = 0;
      setProgress(0);
      setStatus(AsyncDocumentJobStatus.PENDING);

      poll();
    }

    return () => {
      currentJobIdRef.current = null;
      stopPolling();
    };
  }, [jobId, poll, stopPolling]);

  return {
    isPolling,
    progress,
    status,
  };
}

export default useAsyncDocumentPolling;
